import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
  LayoutDashboard,
  UserRound,
  Building2,
  Sparkles,
  CalendarDays,
  Blocks,
  // CreditCard,
  Send,
  LifeBuoy,
  Menu,
  ChevronLeft,
  MessageSquare,
  Megaphone,
  Tag,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { useUiStore } from "@/store/uiStore";
import { useProfileStore } from "@/store/profileStore";
import { useSettingsStore } from "@/store/settingsStore";
import { Logo } from "@/components/atoms/Logo";
import { Tooltip } from "@/components/atoms/Tooltip";
import {
  getBillingAgencyIdSource,
  getBillingEnabledLegalEntityIds,
  isBillingAllowedForAgencyId,
  resolveBillingAgencyId,
} from "@/modules/subscription/billingAccess";
import { billingService } from "@/services/billingService";
import { useCampaignsLabelsEnabled } from "@/modules/campaigns/campaignsLabelsAccess";
import { navTooltipContent, type AppNavShortcutKey } from "@/lib/appNavShortcuts";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  external?: boolean;
  shortcutKey?: AppNavShortcutKey;
}

const NAV: NavItem[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/conversations", label: "Conversations", icon: MessageSquare, shortcutKey: "c" },
  { to: "/reach-out", label: "Reach Out", icon: Send, shortcutKey: "r" },
  { to: "/properties", label: "Properties", icon: Building2, shortcutKey: "p" },
  { to: "/contacts", label: "Contacts", icon: UserRound, shortcutKey: "o" },
  { to: "/leads", label: "Leads", icon: Sparkles, shortcutKey: "l" },
  { to: "/campaigns", label: "Campaigns", icon: Megaphone },
  { to: "/labels", label: "Labels", icon: Tag },
  { to: "/diary", label: "Diary", icon: CalendarDays, shortcutKey: "d" },
  // { to: "/billing/subscription", label: "Subscription", icon: CreditCard },
  { to: "/integrations", label: "Integrations", icon: Blocks, shortcutKey: "i" },
];

function SidebarNavItem({
  item,
  expanded,
  end,
}: {
  item: NavItem;
  expanded: boolean;
  end?: boolean;
}) {
  const className = (isActive: boolean) =>
    cn(
      "group flex items-center gap-3 px-3 h-10 rounded-md text-sm font-medium w-full",
      "transition-[background-color,color,padding] duration-200 ease-in-out",
      "text-gray-700 hover:bg-[#009BF5]/15 hover:text-[#5CC0F8]",
      isActive && "bg-[#009BF5] text-white shadow-sm hover:bg-[#009BF5] hover:text-white",
      !expanded && "justify-center px-0",
    );

  const content = (isActive: boolean) => (
    <>
      <item.icon
        size={18}
        className={cn("shrink-0", isActive ? "text-white" : "text-gray-500 group-hover:text-[#5CC0F8]")}
      />
      <span
        className={cn(
          "overflow-hidden whitespace-nowrap",
          "transition-[opacity,max-width] duration-300 ease-in-out",
          isActive ? "text-white" : "text-gray-700 group-hover:text-[#5CC0F8]",
          expanded ? "opacity-100 max-w-[160px]" : "opacity-0 max-w-0",
        )}
      >
        {item.label}
      </span>
    </>
  );

  const link = item.external ? (
    <a
      href={item.to}
      target="_blank"
      rel="noreferrer"
      className={className(false)}
    >
      {content(false)}
    </a>
  ) : (
    <NavLink
      to={item.to}
      end={end}
      className={({ isActive }) => className(isActive)}
      onClick={() => {
        // Plain sidebar navigation to Conversations should land on the default
        // inbox view, clearing any deep-link target set elsewhere (e.g. Inboxes).
        if (item.to === "/conversations") useUiStore.getState().setChatwootTarget(null);
      }}
    >
      {({ isActive }) => content(isActive)}
    </NavLink>
  );

  const tooltipContent = navTooltipContent(item.label, item.shortcutKey, { expanded });

  return (
    <Tooltip
      content={tooltipContent}
      side="right"
      disabled={expanded}
      className="block w-full"
    >
      <div className="w-full">{link}</div>
    </Tooltip>
  );
}

export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar } = useUiStore();
  const { legalEntityId, branchId } = useSettingsStore();
  const { profileData } = useProfileStore();
  const [billingVisible, setBillingVisible] = useState(false);

  const expanded = !sidebarCollapsed;
  const resolvedBillingAgencyId = useMemo(
    () =>
      resolveBillingAgencyId({
        source: getBillingAgencyIdSource(),
        legalEntityId,
        branchId,
        legalEntities: profileData?.legalEntities ?? [],
        profileContextLoaded: Boolean(profileData),
      }),
    [legalEntityId, branchId, profileData],
  );
  useEffect(() => {
    let cancelled = false;

    async function loadBillingVisibility() {
      if (!resolvedBillingAgencyId) {
        setBillingVisible(false);
        return;
      }

      try {
        const access = await billingService.getSubscriptionAccess(resolvedBillingAgencyId);
        if (!cancelled) setBillingVisible(access.subscriptionManagementEnabled);
      } catch (error) {
        console.warn("[billing] failed to load sidebar billing access; using local fallback", error);
        if (!cancelled) setBillingVisible(isBillingAllowedForAgencyId(resolvedBillingAgencyId));
      }
    }

    void loadBillingVisibility();

    return () => {
      cancelled = true;
    };
  }, [resolvedBillingAgencyId]);

  const billingAllowlistConfigured = getBillingEnabledLegalEntityIds().length > 0;
  const showBilling = billingVisible || (!resolvedBillingAgencyId && !billingAllowlistConfigured);
  const campaignsLabelsEnabled = useCampaignsLabelsEnabled();
  const navItems = NAV.filter((item) => {
    if (!showBilling && item.to === "/billing/subscription") return false;
    if (!campaignsLabelsEnabled && (item.to === "/campaigns" || item.to === "/labels")) return false;
    return true;
  });

  return (
    <aside
      className={cn(
        "h-full flex flex-col py-3 bg-white border-r border-bridge-border",
        "transition-[width] duration-300 ease-in-out overflow-y-auto",
        sidebarCollapsed ? "w-[72px]" : "w-[240px]",
      )}
      style={{ zIndex: 50 }}
    >
      <div className="flex items-center justify-between px-3 mb-6 h-9 overflow-hidden">
        <div className="flex items-center gap-3 overflow-hidden">
          <Tooltip
            content={expanded ? "Collapse sidebar" : "Expand sidebar"}
            side="right"
            disabled={expanded}
          >
            <button
              type="button"
              className="shrink-0 cursor-pointer hover:opacity-80 transition-opacity focus-ring rounded-md"
              onClick={toggleSidebar}
              aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
            >
              {sidebarCollapsed ? (
                <Menu size={24} className="text-bridge-accent ml-1.5" />
              ) : (
                <Logo size={36} />
              )}
            </button>
          </Tooltip>
          {expanded && (
            <div className="leading-tight overflow-hidden whitespace-nowrap animate-in fade-in slide-in-from-left-2 duration-300">
              <div className="text-sm font-bold text-bridge-fg tracking-tight">BRIDGE AI</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-bridge-accent font-semibold">
                ATLAS
              </div>
            </div>
          )}
        </div>
        {expanded && (
          <Tooltip content="Collapse sidebar" side="bottom" disabled>
            <button
              type="button"
              onClick={toggleSidebar}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-bridge-muted transition-colors active:scale-95 focus-ring"
              aria-label="Collapse sidebar"
            >
              <ChevronLeft size={18} />
            </button>
          </Tooltip>
        )}
      </div>

      <nav className="mt-4 flex-1 px-2 space-y-0.5 overflow-visible">
        {navItems.map((item) => (
          <SidebarNavItem key={item.to} item={item} expanded={expanded} end={item.to === "/"} />
        ))}
      </nav>

      <div
        className={cn(
          "mx-3 mb-2 rounded-md bg-bridge-bg border border-bridge-border overflow-hidden",
          "transition-[opacity,max-height,padding] duration-300 ease-in-out",
          expanded ? "opacity-100 max-h-[120px] px-4 py-3" : "opacity-0 max-h-0 px-4 py-0 border-transparent",
        )}
      >
        <Tooltip content="Raise a support ticket" side="right" disabled={expanded}>
          <a
            href="https://2f0til.share-eu1.hsforms.com/2qBtEtzJoQXKbzkLnuk0chQ"
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 w-full py-2 bg-bridge-accent text-white hover:bg-bridge-accentSoft rounded-lg text-[10px] font-bold transition-all shadow-sm"
          >
            <LifeBuoy size={12} />
            <span>RAISE A TICKET</span>
          </a>
        </Tooltip>
      </div>
    </aside>
  );
}
